import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { API_BASE_URL, getUploadUrl } from '@/utils/api';

const MySwal = withReactContent(Swal);

// --- INTERFACES ---
interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  country: string;
  level: string | null;
  package: string | null;
  courseCount?: number;
}

interface Course {
  id: number;
  level: string;
  package_name: string;
  status: string;
  created_at: string;
  certificate_file?: string;
  is_completed?: number;
  completed_at?: string;
  tutor_name?: string;
  content_material?: string;
  session_count?: number;
  total_price?: number;
}

// --- CSS STYLES (Dipertahankan sama persis) ---
const userStyles = `
  :root { --bg-soft: #f8f9fc; --primary-red: #e53935; --primary-hover: #c62828; --text-dark: #2d3436; --text-grey: #636e72; --border-color: #dfe6e9; }
  .creative-container { padding: 30px; background: var(--bg-soft); border-radius: 24px; min-height: 100vh; font-family: 'Plus Jakarta Sans', 'Poppins', sans-serif; }
  .creative-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; flex-wrap: wrap; gap: 20px; }
  .header-actions-group { display: flex; align-items: center; gap: 12px; }
  .title-group h3 { font-size: 1.8rem; font-weight: 800; color: var(--text-dark); margin: 0; letter-spacing: -0.5px; }
  .glass-search { background: white; border-radius: 12px; padding: 12px 20px; box-shadow: 0 4px 20px rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.04); display: flex; align-items: center; width: 320px; transition: 0.3s; }
  .glass-search:focus-within { border-color: var(--primary-red); box-shadow: 0 4px 20px rgba(229, 57, 53, 0.1); }
  .glass-search input { border: none; outline: none; width: 100%; font-size: 0.95rem; color: var(--text-dark); margin-left: 10px; }
  .table-wrapper { overflow-x: auto; padding-bottom: 20px; scrollbar-width: none; }
  .creative-table { width: 100%; border-collapse: separate; border-spacing: 0 15px; }
  .creative-table thead th { font-size: 0.8rem; text-transform: uppercase; letter-spacing: 1px; color: #b2bec3; font-weight: 700; padding: 0 25px 15px; text-align: left; }
  .floating-row td { background: white; padding: 25px; vertical-align: middle; border-top: 1px solid var(--bg-soft); border-bottom: 1px solid var(--bg-soft); transition: transform 0.2s; }
  .floating-row:hover td { background: #fff; transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.02); }
  .floating-row td:first-child { border-top-left-radius: 20px; border-bottom-left-radius: 20px; border-left: 1px solid var(--bg-soft); }
  .floating-row td:last-child { border-top-right-radius: 20px; border-bottom-right-radius: 20px; border-right: 1px solid var(--bg-soft); }
  .user-profile { display: flex; align-items: center; gap: 18px; }
  .avatar-circle { width: 50px; height: 50px; border-radius: 16px; color: white; font-weight: 700; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; box-shadow: 0 8px 15px rgba(0,0,0,0.1); }
  .level-badge { padding: 8px 16px; border-radius: 8px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
  .status-proficient { background: #e3f2fd; color: #1565c0; }
  .status-basic { background: #e8f5e9; color: #2e7d32; }
  .status-independent { background: #fff8e1; color: #fbc02d; }
  .status-empty { background: #f1f2f6; color: #a4b0be; border: 1px dashed #ced6e0; }
  .btn-circle { width: 42px; height: 42px; border-radius: 12px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.3s; background: #fff; border: 1px solid #dfe6e9; color: #636e72; }
  .btn-circle.edit:hover { background: var(--primary-red); color: white; border-color: var(--primary-red); transform: rotate(15deg); }
  .btn-circle.delete:hover { background: #2d3436; color: white; border-color: #2d3436; }
  .btn-header-action { padding: 10px 16px; border-radius: 12px; background: white; border: 1px solid #dfe6e9; color: #636e72; font-weight: 600; font-size: 0.85rem; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: all 0.3s ease; white-space: nowrap; }
  .btn-header-action:hover { border-color: var(--primary-red); color: var(--primary-red); background: #fff5f5; transform: translateY(-2px); }
  .btn-header-action i { font-size: 0.9rem; }
  .btn-header-action.price-btn { border-color: #ffd700; color: #b8860b; }
  .btn-header-action.price-btn:hover { background: #fffdf0; }
  .courses-panel { margin-top: 15px; background: #fff; border-radius: 20px; padding: 25px; border: 1px solid #f1f2f6; box-shadow: inset 0 0 20px rgba(0,0,0,0.01); }
  .course-item { display: flex; justify-content: space-between; align-items: center; padding: 15px 20px; background: #fff; border-radius: 12px; margin-bottom: 12px; border: 1px solid #eee; transition: 0.3s; }
  .course-item:hover { border-color: var(--primary-red); }
  .course-actions { display: flex; gap: 8px; }
  .btn-course-action { width: 32px; height: 32px; border-radius: 8px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.3s; }
  .btn-edit-course { background: #e3f2fd; color: #1976d2; }
  .btn-delete-course { background: #ffebee; color: #e53935; }
  .btn-upload-cert { background: #e8f5e9; color: #2e7d32; }
  .btn-view-cert { background: #fff3e0; color: #ef6c00; }
  .btn-add-course { background: var(--primary-red); color: white; border: none; padding: 10px 20px; border-radius: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px; transition: 0.3s; font-size: 0.9rem; box-shadow: 0 4px 15px rgba(229, 57, 53, 0.3); }
  .btn-add-course:hover { background: var(--primary-hover); transform: translateY(-2px); }
  
  /* SweetAlert Customization */
  div:where(.swal2-container) .swal2-popup { border-radius: 24px !important; padding: 0 !important; overflow: hidden; font-family: 'Poppins', sans-serif; }
  div:where(.swal2-container) .swal2-html-container { margin: 0 !important; padding: 30px !important; }
  div:where(.swal2-container) .swal2-actions { padding: 0 30px 30px 30px !important; margin: 0 !important; gap: 15px; width: 100%; box-sizing: border-box; display: flex; justify-content: flex-end; }
  div:where(.swal2-container) .swal2-confirm { background: var(--primary-red) !important; box-shadow: 0 8px 20px rgba(229, 57, 53, 0.25) !important; border-radius: 12px !important; padding: 14px 28px !important; font-weight: 600 !important; font-size: 1rem !important; flex: 1; }
  div:where(.swal2-container) .swal2-cancel { background: #f1f2f6 !important; color: #636e72 !important; border-radius: 12px !important; padding: 14px 28px !important; font-weight: 600 !important; font-size: 1rem !important; flex: 1; }
  div:where(.swal2-container) .swal2-cancel:hover { background: #dfe6e9 !important; }
  .swal-form-label { display: block; margin-bottom: 8px; font-weight: 700; color: #b2bec3; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 1px; text-align: left; }
  .swal-custom-select-wrapper { position: relative; width: 100%; }
  .swal-custom-select { width: 100%; padding: 14px 18px; border-radius: 12px; border: 2px solid #f1f2f6; background: #fff; font-family: inherit; font-size: 0.95rem; color: var(--text-dark); outline: none; transition: all 0.3s ease; cursor: pointer; appearance: none; -webkit-appearance: none; box-sizing: border-box; }
  .swal-custom-select:focus { border-color: var(--primary-red); box-shadow: 0 0 0 4px rgba(229, 57, 53, 0.1); }
  .swal-custom-select[readonly] { background-color: #f8f9fa; color: #e53935; font-weight: 700; cursor: default; }
  .swal-select-arrow { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); color: #b2bec3; pointer-events: none; font-size: 0.8rem; }
`;

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  const [userCourses, setUserCourses] = useState<{[key: number]: Course[]}>({});
  const [coursesLoading, setCoursesLoading] = useState<{[key: number]: boolean}>({});
  
  // --- STATE FOR DROPDOWNS ---
  const [availableLevels, setAvailableLevels] = useState<string[]>([]);
  const [availablePackages, setAvailablePackages] = useState<string[]>([]);
  const [availableTutors, setAvailableTutors] = useState<string[]>([]);
  
  // --- STATE FOR PRICE (Initial 0, fetched later) ---
  const [basePricePerSession, setBasePricePerSession] = useState<number>(0);

  const API_URL = `${API_BASE_URL}/admin`;
  const USERS_URL = `${API_URL}/users`;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token'); 
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const formatRupiah = (amount: number) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
  };

  // --- 1. FETCH BASE PRICE FROM DB ---
  const fetchBasePrice = async () => {
    try {
      const response = await fetch(`${API_URL}/price`, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      if (result.success) {
        setBasePricePerSession(result.price);
      }
    } catch (error) {
      console.error("Gagal mengambil data harga:", error);
    }
  };

  // --- FETCH ATTRIBUTES ---
  const fetchAttributes = async () => {
    try {
        const response = await fetch(`${API_URL}/attributes`, {
            headers: getAuthHeaders()
        });
        const result = await response.json();
        if (result.success) {
            setAvailableLevels(result.data.levels);
            setAvailablePackages(result.data.packages);
            setAvailableTutors(result.data.tutors);
        }
    } catch (error) {
        console.error("Gagal mengambil data attributes:", error);
    }
  };

  // --- FETCH USERS ---
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(USERS_URL, {
          headers: getAuthHeaders()
      });
      const result = await response.json();
      
      if (result.success) {
        const mappedData = result.data.map((u: any) => ({
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone,
          country: u.country || 'Unknown',
          level: u.level_badge || u.level,
          package: u.package_type || u.package,
          courseCount: 0
        }));
        setUsers(mappedData);
        
        // Fetch courses count for each user
        mappedData.forEach(async (user: User) => {
          try {
            const coursesRes = await fetch(`${API_URL}/users/${user.id}/courses`, {
                headers: getAuthHeaders()
            });
            const coursesData = await coursesRes.json();
            if (coursesData.success) {
              setUsers(prev => prev.map(u => 
                u.id === user.id 
                  ? { ...u, courseCount: coursesData.data.totalCourses }
                  : u
              ));
            }
          } catch (error) {
            console.error(`Error fetching courses for user ${user.id}:`, error);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
      fetchUsers(); 
      fetchAttributes(); 
      fetchBasePrice(); // <--- PANGGIL FETCH HARGA DISINI
  }, []);

  const fetchUserCourses = async (userId: number) => {
    setCoursesLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await fetch(`${API_URL}/users/${userId}/courses`, {
          headers: getAuthHeaders()
      });
      const result = await response.json();
      
      if (result.success) {
        setUserCourses(prev => ({ ...prev, [userId]: result.data.courses }));
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, courseCount: result.data.totalCourses } : u));
      }
    } catch (error) { console.error(error); } 
    finally { setCoursesLoading(prev => ({ ...prev, [userId]: false })); }
  };

  const handleToggleUserCourses = (userId: number) => {
    if (expandedUser === userId) { setExpandedUser(null); } 
    else { setExpandedUser(userId); if (!userCourses[userId]) fetchUserCourses(userId); }
  };

  // --- ADD ATTRIBUTE ---
  const handleAddAttribute = async (type: 'Level' | 'Tutor' | 'Paket') => {
    const { value: newItem } = await Swal.fire({
      title: `Tambah ${type} Baru`,
      input: 'text',
      inputLabel: `Masukkan nama ${type}`,
      showCancelButton: true,
      confirmButtonText: 'Simpan',
      confirmButtonColor: '#e53935',
      showLoaderOnConfirm: true,
      preConfirm: async (inputValue) => {
        if (!inputValue) return Swal.showValidationMessage("Nama tidak boleh kosong");
        
        let dbCategory = '';
        if (type === 'Level') dbCategory = 'level';
        else if (type === 'Tutor') dbCategory = 'tutor';
        else if (type === 'Paket') dbCategory = 'package';

        try {
            const response = await fetch(`${API_URL}/attributes`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ category: dbCategory, name: inputValue })
            });
            const result = await response.json();
            if (!result.success) throw new Error(result.message);
            return inputValue;
        } catch (error: any) {
            Swal.showValidationMessage(`Gagal menyimpan: ${error.message}`);
        }
      }
    });

    if (newItem) {
      if (type === 'Level') setAvailableLevels(prev => [...prev, newItem]);
      else if (type === 'Paket') setAvailablePackages(prev => [...prev, newItem]);
      else if (type === 'Tutor') setAvailableTutors(prev => [...prev, newItem]);
      
      Swal.fire({ icon: 'success', title: 'Berhasil!', text: `${type} "${newItem}" berhasil ditambahkan.`, timer: 1500, showConfirmButton: false });
    }
  };

  // --- 2. EDIT PRICE (UPDATED TO CONNECT DB) ---
  const handleEditBasePrice = async () => {
    const { value: newPrice } = await Swal.fire({
      title: 'Atur Harga Per Sesi',
      input: 'number',
      inputValue: basePricePerSession, // Nilai awal dari DB
      inputLabel: 'Masukkan harga dalam Rupiah (contoh: 25000)',
      showCancelButton: true,
      confirmButtonText: 'Simpan Ke Database',
      confirmButtonColor: '#ffd700',
      cancelButtonText: 'Batal',
      showLoaderOnConfirm: true, // Tampilkan loading saat request
      preConfirm: async (value) => {
        if (!value || Number(value) < 0) return Swal.showValidationMessage('Harga tidak valid!');
        
        try {
          // Request POST ke Backend
          const response = await fetch(`${API_URL}/price`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ price: value })
          });
          const result = await response.json();
          if (!result.success) throw new Error(result.message);
          return value;
        } catch (error: any) {
          Swal.showValidationMessage(`Gagal menyimpan: ${error.message}`);
        }
      }
    });

    if (newPrice) {
      setBasePricePerSession(Number(newPrice));
      Swal.fire({ 
        icon: 'success', 
        title: 'Harga Diperbarui', 
        text: `Harga per sesi sekarang: ${formatRupiah(Number(newPrice))}`, 
        timer: 1500, 
        showConfirmButton: false 
      });
    }
  };

  // --- ADD COURSE (Using Dynamic Price) ---
  const handleAddCourse = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    const initial = user.name.charAt(0).toUpperCase();
    const levelOptions = availableLevels.map(lvl => `<option value="${lvl}">${lvl}</option>`).join('');
    const packageOptions = availablePackages.map(pkg => `<option value="${pkg}">${pkg}</option>`).join('');
    const tutorOptions = availableTutors.map(tutor => `<option value="${tutor}">${tutor}</option>`).join('');

    const { value: formValues } = await Swal.fire({
      title: '', 
      html: `
        <div style="text-align: left;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h3 style="margin: 0; font-size: 1.5rem; font-weight: 800; color: #2d3436;">Tambah Kursus Baru</h3>
            <p style="margin: 5px 0 0; color: #b2bec3; font-size: 0.9rem;">Harga per sesi saat ini: <b>${formatRupiah(basePricePerSession)}</b></p>
          </div>
          <div style="background: #fff; border: 1px solid #f1f2f6; border-radius: 16px; padding: 15px; display: flex; align-items: center; gap: 15px; margin-bottom: 25px; box-shadow: 0 10px 30px rgba(0,0,0,0.03);">
            <div style="width: 50px; height: 50px; background: #e53935; border-radius: 12px; color: white; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.4rem;">${initial}</div>
            <div>
              <div style="font-weight: 700; color: #2d3436; font-size: 1rem;">${user.name}</div>
              <div style="font-size: 0.85rem; color: #636e72;">${user.email}</div>
            </div>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            <div>
              <label class="swal-form-label"><i class="fas fa-layer-group" style="margin-right: 5px; color: #e53935;"></i> Tingkat (Level)</label>
              <div class="swal-custom-select-wrapper"><select id="swal-course-level" class="swal-custom-select"><option value="" disabled selected>Pilih Level...</option>${levelOptions}</select><i class="fas fa-chevron-down swal-select-arrow"></i></div>
            </div>
            <div>
              <label class="swal-form-label"><i class="fas fa-cube" style="margin-right: 5px; color: #e53935;"></i> Jenis Paket</label>
              <div class="swal-custom-select-wrapper"><select id="swal-course-package" class="swal-custom-select"><option value="" disabled selected>Pilih Paket...</option>${packageOptions}</select><i class="fas fa-chevron-down swal-select-arrow"></i></div>
            </div>
            <div>
              <label class="swal-form-label"><i class="fas fa-chalkboard-teacher" style="margin-right: 5px; color: #e53935;"></i> Tutor Pengajar</label>
              <div class="swal-custom-select-wrapper"><select id="swal-course-tutor" class="swal-custom-select"><option value="" disabled selected>Pilih Tutor...</option>${tutorOptions}</select><i class="fas fa-chevron-down swal-select-arrow"></i></div>
            </div>
            <div>
              <label class="swal-form-label"><i class="fas fa-book-open" style="margin-right: 5px; color: #e53935;"></i> Materi / Content</label>
              <input id="swal-course-content" type="text" class="swal-custom-select" placeholder="Ex: General English..." />
            </div>
            <div>
              <label class="swal-form-label"><i class="fas fa-clock" style="margin-right: 5px; color: #e53935;"></i> Jumlah Sesi</label>
              <input id="swal-course-sessions" type="number" min="1" class="swal-custom-select" placeholder="0" />
            </div>
            <div>
              <label class="swal-form-label"><i class="fas fa-tag" style="margin-right: 5px; color: #e53935;"></i> Total Harga</label>
              <input id="swal-course-price" type="text" class="swal-custom-select" readonly value="Rp 0" style="background: #f1f2f6; color: #e53935; font-weight: bold;" />
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Simpan Kursus',
      cancelButtonText: 'Batal',
      buttonsStyling: false,
      customClass: { confirmButton: 'swal2-confirm', cancelButton: 'swal2-cancel', popup: 'swal2-popup', actions: 'swal2-actions' },
      didOpen: () => {
        const sessionInput = document.getElementById('swal-course-sessions') as HTMLInputElement;
        const priceInput = document.getElementById('swal-course-price') as HTMLInputElement;
        sessionInput.addEventListener('input', () => {
          const sessions = parseInt(sessionInput.value) || 0;
          const total = sessions * basePricePerSession; // Menggunakan harga dari DB
          priceInput.value = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(total);
        });
      },
      preConfirm: () => {
        const level = (document.getElementById('swal-course-level') as HTMLSelectElement).value;
        const package_type = (document.getElementById('swal-course-package') as HTMLSelectElement).value;
        const tutor_name = (document.getElementById('swal-course-tutor') as HTMLSelectElement).value; 
        const content_material = (document.getElementById('swal-course-content') as HTMLInputElement).value;
        const session_count = (document.getElementById('swal-course-sessions') as HTMLInputElement).value;
        
        if (!level || !package_type || !tutor_name || !content_material || !session_count) {
          Swal.showValidationMessage('Mohon lengkapi semua data termasuk jumlah sesi');
          return false;
        }
        const sessions = parseInt(session_count);
        const total_price = sessions * basePricePerSession;
        return { level, package_type, tutor_name, content_material, session_count: sessions, total_price, status: 'verified' };
      }
    });
    
    if (formValues) {
      try {
        const response = await fetch(`${API_URL}/users/${userId}/courses`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(formValues)
        });
        const result = await response.json();
        
        if (result.success) {
          Swal.fire({ icon: 'success', title: 'Berhasil!', text: `Kursus ditambahkan. Total: ${formatRupiah(formValues.total_price)}`, timer: 2000, showConfirmButton: false });
          fetchUserCourses(userId);
        } else {
          throw new Error(result.message);
        }
      } catch (error: any) {
        Swal.fire({ icon: 'error', title: 'Gagal', text: error.message || 'Gagal menambahkan kursus' });
      }
    }
  };

  // --- EDIT COURSE ---
  const handleEditCourse = async (userId: number, courseId: number, currentCourse: Course) => {
    const levelOptions = availableLevels.map(lvl => `<option value="${lvl}" ${currentCourse.level === lvl ? 'selected' : ''}>${lvl}</option>`).join('');
    const packageOptions = availablePackages.map(pkg => `<option value="${pkg}" ${currentCourse.package_name === pkg ? 'selected' : ''}>${pkg}</option>`).join('');

    const { value: formValues } = await Swal.fire({
      title: 'Edit Kursus',
      html: `
        <div style="text-align:left; padding:10px;">
          <div style="margin-bottom:15px;">
            <label class="swal-form-label">Level</label>
            <div class="swal-custom-select-wrapper"><select id="swal-edit-level" class="swal-custom-select">${levelOptions}</select></div>
          </div>
          <div style="margin-bottom:15px;">
            <label class="swal-form-label">Paket</label>
             <div class="swal-custom-select-wrapper"><select id="swal-edit-package" class="swal-custom-select">${packageOptions}</select></div>
          </div>
          <div style="margin-bottom:15px;">
            <label class="swal-form-label">Status</label>
             <div class="swal-custom-select-wrapper">
              <select id="swal-edit-status" class="swal-custom-select">
                <option value="verified" ${currentCourse.status === 'verified' ? 'selected' : ''}>Verified</option>
                <option value="pending" ${currentCourse.status === 'pending' ? 'selected' : ''}>Pending</option>
                <option value="cancelled" ${currentCourse.status === 'cancelled' ? 'selected' : ''}>Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Simpan',
      cancelButtonText: 'Batal',
      buttonsStyling: false,
      customClass: { confirmButton: 'swal2-confirm', cancelButton: 'swal2-cancel', popup: 'swal2-popup', actions: 'swal2-actions' },
      preConfirm: () => {
        return { 
          level: (document.getElementById('swal-edit-level') as HTMLSelectElement).value,
          package_name: (document.getElementById('swal-edit-package') as HTMLSelectElement).value,
          status: (document.getElementById('swal-edit-status') as HTMLSelectElement).value
        };
      }
    });
    
    if (formValues) {
      try {
        const response = await fetch(`${API_URL}/users/${userId}/courses/${courseId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(formValues)
        });
        const result = await response.json();
        if (result.success) {
          Swal.fire({ icon: 'success', title: 'Berhasil!', text: 'Kursus diperbarui', timer: 2000 });
          setUserCourses(prev => ({ ...prev, [userId]: prev[userId].map(course => course.id === courseId ? { ...course, ...formValues } : course) }));
        } else { throw new Error(result.message); }
      } catch (error: any) { Swal.fire({ icon: 'error', title: 'Gagal', text: error.message }); }
    }
  };

  // --- DELETE COURSE ---
  const handleDeleteCourse = async (userId: number, courseId: number) => {
    const result = await Swal.fire({ title: 'Hapus Kursus?', text: 'Kursus ini akan dihapus permanen', icon: 'warning', showCancelButton: true, confirmButtonText: 'Ya, Hapus', cancelButtonText: 'Batal', buttonsStyling: false, customClass: { confirmButton: 'swal2-confirm', cancelButton: 'swal2-cancel', popup: 'swal2-popup', actions: 'swal2-actions' } });
    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/users/${userId}/courses/${courseId}`, { 
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        const data = await response.json();
        if (data.success) {
          Swal.fire({ icon: 'success', title: 'Terhapus!', timer: 2000 });
          setUserCourses(prev => ({ ...prev, [userId]: prev[userId].filter(course => course.id !== courseId) }));
          setUsers(prev => prev.map(u => u.id === userId ? { ...u, courseCount: (u.courseCount || 1) - 1 } : u));
        } else { throw new Error(data.message); }
      } catch (error: any) { Swal.fire({ icon: 'error', title: 'Gagal', text: error.message }); }
    }
  };

  // --- UPLOAD CERTIFICATE ---
  const handleUploadCertificate = async (userId: number, courseId: number) => {
    const { value: file } = await Swal.fire({ title: 'Upload Sertifikat', input: 'file', inputAttributes: { accept: 'image/*,.pdf', 'aria-label': 'Upload sertifikat' }, showCancelButton: true, confirmButtonText: 'Upload', cancelButtonText: 'Batal', buttonsStyling: false, customClass: { confirmButton: 'swal2-confirm', cancelButton: 'swal2-cancel', popup: 'swal2-popup', actions: 'swal2-actions' } });
    if (file) {
      const formData = new FormData();
      formData.append('certificate', file);
      try {
        const response = await fetch(`${API_URL}/courses/certificates/${userId}/course/${courseId}/upload`, { 
            method: 'POST', 
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData 
        });
        const result = await response.json();
        if (result.success) { Swal.fire({ icon: 'success', title: 'Berhasil!', timer: 2000 }); fetchUserCourses(userId); }
        else { throw new Error(result.message); }
      } catch (error: any) { Swal.fire({ icon: 'error', title: 'Gagal', text: error.message }); }
    }
  };

  const handleViewCertificate = (certificateFile: string) => { if (certificateFile) window.open(getUploadUrl(`uploads/certificates/${certificateFile}`), '_blank'); };

  // --- DELETE USER ---
  const handleDeleteUser = async (userId: number) => {
    const result = await Swal.fire({ title: 'Hapus Siswa?', text: 'Semua data kursus dan sertifikat dihapus!', icon: 'warning', showCancelButton: true, confirmButtonText: 'Ya, Hapus', cancelButtonText: 'Batal', confirmButtonColor: '#e53935', buttonsStyling: false, customClass: { confirmButton: 'swal2-confirm', cancelButton: 'swal2-cancel', popup: 'swal2-popup', actions: 'swal2-actions' } });
    if (result.isConfirmed) {
      try {
        const response = await fetch(`${USERS_URL}/${userId}`, { 
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        const data = await response.json();
        if (data.success) { Swal.fire({ icon: 'success', title: 'Terhapus!', timer: 2000, showConfirmButton: false }); setUsers(prev => prev.filter(u => u.id !== userId)); }
        else { throw new Error(data.message); }
      } catch (error: any) { Swal.fire({ icon: 'error', title: 'Gagal', text: error.message }); }
    }
  };

  const getAvatarColor = (name: string) => { const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9B59B6']; let hash = 0; for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash); return colors[Math.abs(hash) % colors.length]; };
  const getInitials = (name: string) => name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();

  const filteredUsers = users.filter((u) => u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase()));

  const UserCoursesPanel: React.FC<{userId: number}> = ({ userId }) => {
    const courses = userCourses[userId] || [];
    const isLoading = coursesLoading[userId];
    if (isLoading) return <div className="courses-panel"><div style={{textAlign: 'center', padding: '20px', color: '#636e72'}}><i className="fas fa-spinner fa-spin"></i> Memuat kursus...</div></div>;
    return (
      <div className="courses-panel">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
          <h4 style={{margin: 0, fontSize: '1rem', fontWeight: '600', color: '#2d3436'}}>📚 Daftar Kursus ({courses.length} kursus)</h4>
          <button className="btn-add-course" onClick={() => handleAddCourse(userId)}><i className="fas fa-plus"></i> Tambah Kursus</button>
        </div>
        {courses.length > 0 ? (
          <div>
            {courses.map(course => (
              <div key={course.id} className="course-item">
                <div className="course-info">
                  <div className="course-title">
                    {course.level} - {course.package_name}
                    {course.certificate_file && <span className="certificate-badge"><i className="fas fa-certificate"></i> Sertifikat</span>}
                  </div>
                  <div className="course-meta">
                    <span className={`badge-status status-${course.status}`}>{course.status}</span>
                    <span style={{marginRight: '15px'}}>ID: {course.id}</span>
                    <span style={{marginRight: '15px'}}>Dibuat: {new Date(course.created_at).toLocaleDateString('id-ID')}</span>
                    {course.session_count && <span style={{marginRight: '15px', color: '#e53935', fontWeight: 'bold'}}>{course.session_count} Sesi ({formatRupiah(course.total_price || 0)})</span>}
                    {course.completed_at && <span style={{color: '#4CAF50'}}><i className="fas fa-check-circle"></i> Selesai: {new Date(course.completed_at).toLocaleDateString('id-ID')}</span>}
                  </div>
                </div>
                <div className="course-actions">
                  <button className="btn-course-action btn-edit-course" onClick={() => handleEditCourse(userId, course.id, course)} title="Edit kursus"><i className="fas fa-edit"></i></button>
                  {course.certificate_file ? <button className="btn-course-action btn-view-cert" onClick={() => handleViewCertificate(course.certificate_file || '')} title="Lihat sertifikat"><i className="fas fa-eye"></i></button> : <button className="btn-course-action btn-upload-cert" onClick={() => handleUploadCertificate(userId, course.id)} title="Upload sertifikat"><i className="fas fa-upload"></i></button>}
                  <button className="btn-course-action btn-delete-course" onClick={() => handleDeleteCourse(userId, course.id)} title="Hapus kursus"><i className="fas fa-trash"></i></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{textAlign: 'center', padding: '30px', color: '#b2bec3', fontStyle: 'italic'}}>
            <i className="fas fa-book" style={{fontSize: '2rem', marginBottom: '10px', display: 'block'}}></i>
            Belum ada kursus untuk user ini
            <div style={{marginTop: '10px'}}><button className="btn-add-course" onClick={() => handleAddCourse(userId)}><i className="fas fa-plus"></i> Tambah Kursus Pertama</button></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{userStyles}</style>
      <div className="creative-container">
        <div className="creative-header">
          <div className="title-group">
            <h3>Daftar Siswa</h3>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px'}}>
              <small style={{color: '#636e72', fontSize: '0.85rem'}}>Total: {users.length} siswa • Total kursus: {users.reduce((sum, user) => sum + (user.courseCount || 0), 0)}</small>
              <button onClick={() => { fetchUsers(); fetchAttributes(); fetchBasePrice(); }} style={{padding: '5px 15px', background: '#f1f2f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px'}} title="Refresh data"><i className="fas fa-sync-alt"></i> Refresh</button>
            </div>
          </div>

          <div className="header-actions-group">
            <button className="btn-header-action" onClick={() => handleAddAttribute('Level')}><i className="fas fa-layer-group"></i> Tambah Level</button>
            <button className="btn-header-action" onClick={() => handleAddAttribute('Tutor')}><i className="fas fa-chalkboard-teacher"></i> Tambah Tutor</button>
            <button className="btn-header-action" onClick={() => handleAddAttribute('Paket')}><i className="fas fa-box"></i> Tambah Paket</button>
            
            {/* BUTTON ATUR HARGA - MEMICU FUNGSI DB */}
            <button className="btn-header-action price-btn" onClick={handleEditBasePrice}><i className="fas fa-tags"></i> Atur Harga Sesi</button>
            
            <div className="glass-search">
              <i className="fas fa-search" style={{color: '#b2bec3', marginRight: '8px'}}></i>
              <input type="text" placeholder="Cari nama atau email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#b2bec3', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'}}><i className="fas fa-spinner fa-spin" style={{fontSize: '2rem'}}></i><div>Memuat data siswa...</div></div>
        ) : (
          <div className="table-wrapper">
            <table className="creative-table">
              <thead><tr><th>Siswa</th><th>Kontak</th><th>Level & Paket</th><th>Kursus</th><th>Aksi</th></tr></thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr className="floating-row" onClick={() => handleToggleUserCourses(user.id)} style={{cursor: 'pointer'}}>
                      <td>
                        <div className="user-profile">
                          <div className="avatar-circle" style={{ background: getAvatarColor(user.name) }}>{getInitials(user.name)}</div>
                          <div>
                            <h4 style={{margin: 0, fontSize: '1rem'}}>{user.name}</h4>
                            <small style={{color: '#636e72', fontSize: '0.8rem'}}>{user.country} • ID: {user.id}</small>
                            <div style={{display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px'}}>
                              <i className="fas fa-chevron-right" style={{transform: expandedUser === user.id ? 'rotate(90deg)' : 'rotate(0deg)', transition: '0.3s', fontSize: '0.8rem', color: '#e53935'}}></i>
                              <span style={{fontSize: '0.7rem', color: '#636e72'}}>Klik untuk {expandedUser === user.id ? 'tutup' : 'lihat'} kursus</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td><div style={{fontWeight: '500'}}>{user.email}</div><small style={{color: '#636e72'}}>{user.phone || 'No Phone'}</small></td>
                      <td>
                        {user.level ? <span className={`level-badge status-${user.level.toLowerCase()}`}>{user.level}</span> : <span className="level-badge status-empty">Belum Ada</span>}
                        <div style={{fontSize: '0.85rem', marginTop: '5px', color: '#636e72'}}>{user.package || 'Belum memilih paket'}</div>
                      </td>
                      <td>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <div style={{width: '24px', height: '24px', borderRadius: '50%', background: user.courseCount && user.courseCount > 0 ? '#4CAF50' : '#f1f2f6', color: user.courseCount && user.courseCount > 0 ? 'white' : '#636e72', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold'}}>{user.courseCount || 0}</div>
                          <span style={{fontSize: '0.9rem'}}>{user.courseCount === 1 ? '1 kursus' : `${user.courseCount || 0} kursus`}</span>
                        </div>
                        <button onClick={(e) => {e.stopPropagation(); handleAddCourse(user.id);}} style={{marginTop: '5px', padding: '3px 10px', fontSize: '0.7rem', background: '#4CAF50', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px'}}><i className="fas fa-plus" style={{fontSize: '0.6rem'}}></i> Tambah Kursus</button>
                      </td>
                      <td>
                        <div style={{display: 'flex', gap: '10px'}}>
                          <button className="btn-circle delete" onClick={(e) => {e.stopPropagation(); handleDeleteUser(user.id);}} title="Hapus Siswa"><i className="fas fa-trash-alt"></i></button>
                        </div>
                      </td>
                    </tr>
                    {expandedUser === user.id && <tr><td colSpan={5} style={{padding: 0, border: 'none'}}><UserCoursesPanel userId={user.id} /></td></tr>}
                  </React.Fragment>
                ))}
                {filteredUsers.length === 0 && <tr><td colSpan={5} style={{textAlign: 'center', padding: '40px', color: '#b2bec3', fontStyle: 'italic'}}><i className="fas fa-user-slash" style={{fontSize: '2rem', marginBottom: '10px', display: 'block'}}></i>Tidak ada siswa yang ditemukan{searchQuery && <div style={{marginTop: '10px'}}>Coba dengan kata kunci lain</div>}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Users;